import { action, getRoot, Instance, register, types } from "@gadgetinc/mobx-quick-tree";
import { observable } from "mobx";
import { LiveRoot } from "../../LiveRoot";
import { ProblemsList } from "../../problems/ProblemsList";
import { ShopifyTomlConfigProblems } from "../../problems/finders/ShopifyConfigProblems";
import { contentHash, GadgetDomainModelClass, getAppSettings, isLiveRoot } from "../../utils";
import { ShopifyConnectionConfig } from "../ShopifyConnectionConfig";
import { getDefaultShopifyRedirectUrl } from "./ShopifyDefaultUrls";

@register
export class ShopifyTomlConfig extends GadgetDomainModelClass("ShopifyTomlConfig", {
  shopifyApiVersion: types.maybe(types.string),
  appName: types.maybe(types.string),
  appUrl: types.maybe(types.string),
  embedded: types.maybe(types.boolean),
  clientId: types.maybe(types.string),
  redirectUrls: types.maybe(types.array(types.string)),
  scopes: types.maybe(types.array(types.string)),
  includeConfigOnDeploy: types.optional(types.boolean, false),
  linkedTomlFile: types.optional(types.string, ""),
}) {
  get name() {
    return "Shopify Toml";
  }
  get defaultApiIdentifier() {
    return "shopifyToml";
  }
  get contentHash() {
    return contentHash([this.type, this.appName, this.embedded, this.clientId]);
  }
  get problems(): Instance<typeof ProblemsList> {
    const problems = ShopifyTomlConfigProblems(this as any);
    return problems;
  }
  get connectionConfig(): Instance<typeof ShopifyConnectionConfig> | undefined {
    const root = getRoot(this);
    const connection = root.maybeGetShopifyConnection();
    return connection?.configuration;
  }

  get defaultShopifyRedirectUrls() {
    const root = getRoot(this) as Instance<typeof LiveRoot>;
    const settings = getAppSettings(root);

    return getDefaultShopifyRedirectUrl(settings.defaultDomain).map((url) => url.toString());
  }

  get hasLinkedToml() {
    const root = getRoot(this) as Instance<typeof LiveRoot>;
    return root.doesFileExist(this.linkedTomlFile);
  }

  get hasExtensionsToml() {
    const root = getRoot(this) as Instance<typeof LiveRoot>;
    if (!isLiveRoot(root)) {
      return;
    }
    return root.hasExtensionsToml();
  }

  get hasMatchingClientId() {
    const connectionConfig = this.connectionConfig;
    if (!connectionConfig || !this.clientId) return false;

    const appConfig = connectionConfig.getAppConfigByApiKey(this.clientId);

    return this.clientId === appConfig?.apiKey;
  }

  get shouldShowProblems() {
    return this.hasLinkedToml && this.hasMatchingClientId && (this.hasExtensionsToml || this.includeConfigOnDeploy);
  }

  get sourceLocation() {
    return {
      filePath: this.linkedTomlFile,
      language: "toml",
      lineNumber: 0,
      startColumn: 0,
      startLineNumber: 0,
      endColumn: 0,
      endLineNumber: 0,
    };
  }
  @action
  setShopifyApiVersion(value: string) {
    this.shopifyApiVersion = value;
  }
  @action
  setClientId(value?: string) {
    if (value) {
      this.clientId = value;
    } else {
      this.clientId = undefined;
    }
  }
  @action
  setLinkedTomlFile(value?: string) {
    if (value) {
      this.linkedTomlFile = value;
    } else {
      this.linkedTomlFile = "";
    }
  }
  @action
  setApiVersion(value: string) {
    this.shopifyApiVersion = value;
  }
  @action
  setAppName(value: string) {
    this.appName = value;
  }
  @action
  setAppUrl(value: string) {
    this.appUrl = value;
  }
  @action
  setEmbedded(value: boolean) {
    this.embedded = value;
  }
  @action
  setIncludeConfigOnDeploy(value: boolean) {
    this.includeConfigOnDeploy = value;
  }
  @action
  setScopes(value?: string[]) {
    if (value) {
      if (this.scopes) {
        this.scopes.replace(value);
      } else {
        this.scopes = observable.array(value);
      }
    }
  }

  @action
  setRedirectUrls(value?: string[]) {
    if (value) {
      if (this.redirectUrls) {
        this.redirectUrls.replace(value);
      } else {
        this.redirectUrls = observable.array(value);
      }
    }
  }

  @action
  removeRedirectUrls() {
    if (this.redirectUrls) {
      this.redirectUrls = undefined;
    }
  }

  @action
  removeScopes() {
    if (this.scopes) {
      this.scopes = undefined;
    }
  }

  @action
  clear() {
    this.clientId = undefined;
    this.linkedTomlFile = "";
    this.shopifyApiVersion = undefined;
    this.appName = undefined;
    this.appUrl = undefined;
    this.redirectUrls = undefined;
    this.scopes = undefined;
    this.embedded = undefined;
  }
}
